/**
 * Browser-side UILM **write** API used only from admin routes.
 *
 * Uses the project's authenticated HTTP client (`clients`) which already
 * attaches `Authorization: Bearer <jwt>` from the auth store AND
 * `x-blocks-key` from env on every request. Callers do NOT need to wire
 * auth themselves — but they MUST ensure the user is an admin before
 * invoking these functions (gated at the route + page level).
 *
 * Endpoint shapes follow the UILM v1 Swagger spec. See
 * `.claude/skills/blocks-uilm/contracts.md` for the canonical reference.
 */

import { clients } from '@/lib/https';
import {
  DEFAULT_LANGUAGE_CULTURE,
  UILM_FETCH_PAGE_SIZE,
} from '../lib/uilm-constants';

const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface UilmResource {
  value: string;
  culture: string;
  characterLength: number;
}

export interface UilmKey {
  itemId: string;
  keyName: string;
  moduleId: string;
  resources: UilmResource[];
  isPartiallyTranslated?: boolean;
  context?: string;
}

interface ApiResponse {
  success: boolean;
  errorMessage: string | null;
  validationErrors: unknown[];
}

interface GetKeysByNamesResponse {
  keys: UilmKey[];
  errorMessage: string | null;
}

interface GetKeysResponse {
  totalCount: number;
  keys: UilmKey[];
}

// ---------------------------------------------------------------------------
// Modules
// ---------------------------------------------------------------------------

/**
 * Create (or update) a UILM module by name. Idempotent on the name —
 * calling twice with the same `moduleName` does not error.
 */
export const saveUilmModule = async (moduleName: string): Promise<void> => {
  await clients.post<ApiResponse>(
    '/uilm/v1/Module/Save',
    JSON.stringify({ moduleName, projectKey }),
  );
};

// ---------------------------------------------------------------------------
// Keys — reads
// ---------------------------------------------------------------------------

/**
 * Look up keys by name. If `moduleId` is omitted the search spans every
 * module — useful for discovering which module a template's title key
 * already lives in (the basis of module discovery for legacy templates).
 */
export const getUilmKeysByNames = async (
  moduleId: string | undefined,
  keyNames: string[],
): Promise<UilmKey[]> => {
  if (keyNames.length === 0) return [];
  const res = await clients.post<GetKeysByNamesResponse>(
    '/uilm/v1/Key/GetsByKeyNames',
    JSON.stringify({ projectKey, moduleId, keyNames }),
  );
  return res?.keys ?? [];
};

/**
 * Fetch every key in a module. Used by the translations-tab diff to
 * compare local-vs-remote. Pages are large because templates with
 * hundreds of keys are uncommon; bump `UILM_FETCH_PAGE_SIZE` rather
 * than paginating if a template ever overflows.
 *
 * Pagination is `pageNumber` (NOT `pageNo`) and is **0-indexed** — page
 * 0 is the first page. Passing `pageNumber: 1` returns the SECOND page
 * which yields `totalCount > 0, keys: []` for small modules and looks
 * like a filter bug. Verified against the live API via Postman.
 */
export const getUilmKeysByModule = async (moduleId: string): Promise<UilmKey[]> => {
  const res = await clients.post<GetKeysResponse>(
    '/uilm/v1/Key/Gets',
    JSON.stringify({
      projectKey,
      moduleIds: [moduleId],
      pageNumber: 0,
      pageSize: UILM_FETCH_PAGE_SIZE,
    }),
  );
  return res?.keys ?? [];
};

// ---------------------------------------------------------------------------
// Keys — writes
// ---------------------------------------------------------------------------

/**
 * Save a single key.
 *
 * `isNewKey` MUST reflect whether the key already exists on UILM:
 *   - `true`  → creates the key. UILM rejects with `AsyncPredicateValidator`
 *               if a key with the same name+module already exists.
 *   - `false` → updates the existing key. UILM rejects similarly if the
 *               key does NOT yet exist (though this path is less common).
 * Callers who don't know the state should look it up via
 * `getUilmKeysByNames` first — that's what `pushKeysPreservingNonEn`
 * does for the per-entity sync flows.
 *
 * `shouldPublish` defaults to **true** — a one-shot save publishes itself
 * so the new value is immediately visible on the CDN. Callers that issue
 * many saves in a row (e.g. the bulk-seed loop) should pass `false` on
 * every save and call `generateUilmFile` once at the end instead, to
 * avoid N concurrent file regenerations racing with each other. The
 * batched `saveUilmKeys` helper already does that internally.
 */
export const saveUilmKey = async (
  moduleId: string,
  keyName: string,
  resources: UilmResource[],
  shouldPublish = true,
  isNewKey = true,
): Promise<void> => {
  await clients.post<ApiResponse>(
    '/uilm/v1/Key/Save',
    JSON.stringify({
      projectKey,
      moduleId,
      keyName,
      resources,
      isNewKey,
      shouldPublish,
    }),
  );
};

/**
 * Concurrency cap for `saveUilmKeys`. Matches the pattern in
 * `scripts/uilm-sync.mjs` so a large template doesn't fire hundreds of
 * concurrent requests at the UILM gateway.
 */
const SAVE_CONCURRENCY = 10;

/**
 * "Batch"-save many keys. Implemented as a bounded-concurrency loop over
 * the singular `/Key/Save` endpoint because the plural `/Key/SaveKeys`
 * binder rejects the envelope shape documented in the skill — its
 * actual contract expects each item to be a fully self-contained `Key`
 * (with `projectKey`/`moduleId`/`isNewKey` on every item), which makes
 * it strictly more verbose than calling `Key/Save` per key. So we use
 * the singular endpoint that the production sync script already proves
 * out and chunk for parallelism instead.
 *
 * Each item carries its own `isNewKey` flag because a single batch can
 * mix new keys (e.g. an admin just added a screen, options below it are
 * new) with updates of existing ones (the screen title key already on
 * UILM). Callers default to `true` for "this is definitely new" and
 * `false` for "this is definitely an update".
 *
 * `shouldPublish` defaults to **true** — the LAST save in the batch
 * carries `shouldPublish: true` so UILM regenerates the file as part of
 * that final write, saving a round-trip on the common per-entity sync
 * path (1 key in, 1 `/Key/Save` out, no separate `GenerateUilmFile`).
 * All earlier saves run in chunked parallel with `shouldPublish: false`
 * — issuing N concurrent regenerations for one batch would race on the
 * CDN file. The seed flow opts out entirely (`shouldPublish: false`)
 * and runs its own deferred `generateUilmFile` so it can sequence other
 * work (e.g. UDS back-fill) in between.
 */
export const saveUilmKeys = async (
  moduleId: string,
  keys: { keyName: string; resources: UilmResource[]; isNewKey?: boolean }[],
  shouldPublish = true,
): Promise<void> => {
  if (keys.length === 0) return;
  // Save every key but the last in chunked parallel — never with publish,
  // to avoid the concurrent-regen race.
  const headKeys = keys.slice(0, -1);
  for (let i = 0; i < headKeys.length; i += SAVE_CONCURRENCY) {
    const chunk = headKeys.slice(i, i + SAVE_CONCURRENCY);
    await Promise.all(
      chunk.map((k) =>
        saveUilmKey(moduleId, k.keyName, k.resources, false, k.isNewKey ?? true),
      ),
    );
  }
  // Save the final key alone so its `shouldPublish` flag — whatever the
  // caller wanted — lands deterministically after all the other writes
  // finished. UILM regenerates the file in response to this single save
  // when `shouldPublish: true`, so callers don't need a follow-up call.
  const last = keys[keys.length - 1];
  await saveUilmKey(
    moduleId,
    last.keyName,
    last.resources,
    shouldPublish,
    last.isNewKey ?? true,
  );
};

// ---------------------------------------------------------------------------
// File lifecycle
// ---------------------------------------------------------------------------

/**
 * Re-generate the cached UILM file for a module. Run this once after a
 * batch of saves so the new keys become available via `GetUilmFile`
 * (i.e. visible to the runtime). Without this step, saves persist on
 * UILM but the CDN keeps serving the previous snapshot.
 *
 * The `guid` field is a per-request correlation token (NOT a user id),
 * same role as `messageCoRelationId` on the translate endpoints. The
 * skill docs mark it optional but the actual server returns 400 if it's
 * missing, so we always supply a fresh UUID.
 */
export const generateUilmFile = async (moduleId: string): Promise<void> => {
  await clients.post<ApiResponse>(
    '/uilm/v1/Key/GenerateUilmFile',
    JSON.stringify({
      projectKey,
      moduleId,
      guid: crypto.randomUUID(),
    }),
  );
};

/**
 * Delete a single UILM key by its itemId. Used to clean up orphan keys
 * surfaced in the Translations Tab. Irreversible — caller should confirm
 * with the admin before invoking.
 */
export const deleteUilmKey = async (itemId: string): Promise<void> => {
  const params = new URLSearchParams({
    ItemId: itemId,
    ProjectKey: projectKey,
  });
  await clients.delete<ApiResponse>(`/uilm/v1/Key/Delete?${params.toString()}`);
};

/**
 * Kick off an AI auto-translate job for a module. Returns immediately —
 * UILM runs the translation server-side and the new resources appear
 * on subsequent reads, but the cached file is NOT regenerated; the
 * admin or app must call `generateUilmFile` to publish.
 *
 * `messageCoRelationId` is the join key for the eventual webhook
 * callback. Callers that track job state (e.g. our `UilmTranslationJob`
 * UDS rows) MUST pass their own UUID so the webhook receiver can match
 * the inbound event back to the job row. If omitted we generate one,
 * which is fine for fire-and-forget jobs but loses traceability.
 */
export const translateAll = async (
  moduleId: string,
  defaultLanguage: string = DEFAULT_LANGUAGE_CULTURE,
  messageCoRelationId: string = crypto.randomUUID(),
): Promise<void> => {
  await clients.post<ApiResponse>(
    '/uilm/v1/Key/TranslateAll',
    JSON.stringify({
      projectKey,
      moduleId,
      defaultLanguage,
      messageCoRelationId,
    }),
  );
};
